/**
 * The drawing's objects, ONE adapter per kind (`ItemKind`): where a kind's
 * objects live in the Drawing (the same key carries its copies on the
 * clipboard), its new ids' prefix, its points and outline, and how it moves,
 * transforms (a point map, a turn and a scale), mirrors, rises, is copied
 * (what Copy takes of a selection, fresh ids, its references pointed at the
 * copies, what a paste selects) and is deleted (what goes with it). The move /
 * transform / mirror / copy / delete code and the outline read their adapter,
 * so a new kind does not compile until it says how it does each
 * (docs/claude/objects.md).
 *
 * Pure and DOM-free (unit-tested in test/drawingObjects.test.mjs): what only
 * main.ts knows — a part's drawn outline, a room's re-derive, a schedule table's
 * place on the screen — comes in through `ObjectServices`.
 */
import type {
  Arc,
  CeilingPlanView,
  Connection,
  DimAnchor,
  DimensionLine,
  Drawing,
  DrawingGroup,
  Flooring,
  Leader,
  ObjectConstraint,
  Polygon,
  ProductInstance,
  RevisionCloud,
  ScheduleTable,
  SectionLine,
  TextComment,
  Vec2,
  Wall,
} from '../shared/types';
import type { ItemKind } from './layers';
import {
  axisAngleDeg,
  mirroredRotationDeg,
  reflectAcross,
  rotatePoint,
} from './geometry';
import {
  moveTextBoxCentreTo,
  rotateTextBoxTo,
  textBoxCentre,
  textBoxCorners,
} from './textBox';
import {
  polygonBase,
  polygonHeight,
  wallBase,
  wallHeight,
} from './heights';
import {
  wallBand,
  wallSnapVerts,
} from './wallTool';
import {
  kitProjectorIds,
  mirrorRoomLights,
  relinkKits,
} from './lightLife';
import {
  groupMembers,
  isAutoGroupName,
  normalizeGroups,
  outermostGroup,
  pruneRelations,
} from './groups';
import { mirrorCeiling } from './ceiling';
import { remintFeatureIds } from './polyRoom';
import { anchorObjectId } from './anchors';

/** What each kind's objects are. */
export interface KindObjects {
  arc: Arc;
  cloud: RevisionCloud;
  conn: Connection;
  dim: DimensionLine;
  flooring: Flooring;
  instance: ProductInstance;
  leader: Leader;
  polygon: Polygon;
  rcp: CeilingPlanView;
  section: SectionLine;
  table: ScheduleTable;
  text: TextComment;
  wall: Wall;
}

/** Where each kind's objects live in a drawing, in the order the layers list
 *  them — and the key its copies travel under on the clipboard. `table` has no
 *  array: its objects are the drawing's schedule tables (layers.ts TABLE_IDS),
 *  and it is not copied. */
export const OBJECT_ARRAY = {
  polygon: 'polygons',
  wall: 'walls',
  flooring: 'floorings',
  instance: 'instances',
  conn: 'connections',
  section: 'sections',
  rcp: 'ceilingPlans',
  text: 'texts',
  arc: 'arcs',
  leader: 'leaders',
  cloud: 'revisionClouds',
  table: null,
  dim: 'dimensions',
} as const satisfies Record<ItemKind, keyof Drawing | null>;

/** A kind whose objects live in an array of the drawing (all but the tables). */
export type StoredKind = { [K in ItemKind]: (typeof OBJECT_ARRAY)[K] extends null ? never : K }[ItemKind];
export type ArrayKey = NonNullable<(typeof OBJECT_ARRAY)[ItemKind]>;
/** Every stored kind's objects under its array key: what a copy carries. */
export type ObjectArrays = { [K in StoredKind as (typeof OBJECT_ARRAY)[K]]: KindObjects[K][] };

export const STORED_KINDS: readonly StoredKind[] = (Object.keys(OBJECT_ARRAY) as ItemKind[]).filter((k): k is StoredKind => OBJECT_ARRAY[k] !== null);

/** A kind's objects in a drawing or a copy's payload (none for the tables). */
export function objectsOf<K extends ItemKind>(d: Partial<Record<ArrayKey, unknown>>, kind: K): KindObjects[K][] | undefined {
  const key = OBJECT_ARRAY[kind];
  return key ? (d[key] as KindObjects[K][] | undefined) : undefined;
}

/** Every stored kind's array, empty. */
export function emptyObjectArrays(): ObjectArrays {
  return Object.fromEntries(STORED_KINDS.map((k) => [OBJECT_ARRAY[k], []])) as unknown as ObjectArrays;
}

/** One object with its kind. */
export type ObjItem = { [K in ItemKind]: { kind: K; obj: KindObjects[K] } }[ItemKind];
/** One object of a stored kind (all but a schedule table), with its kind. */
export type StoredItem = Extract<ObjItem, { kind: StoredKind }>;

/** A point map, with what a point map cannot carry: the turn it makes (degrees
 *  CCW: a part's or a text box's angle, an arc's sweep) and its scale (a size).
 *  `foot`: it scales heights too (a section view's Scale) — each base about
 *  `foot` (mm above the ground), each height ×scale; absent, heights stay. */
export interface PointMap { fn: (p: Vec2) => Vec2; rot: number; scale: number; foot?: number }
export const translation = (dx: number, dy: number): PointMap => ({ fn: (p) => ({ x: p.x + dx, y: p.y + dy }), rot: 0, scale: 1 });
/** A scale ×k about `c` on the plan (Transform ▸ Scale's). */
export const scaling = (c: Vec2, k: number): PointMap => ({ fn: (p) => ({ x: c.x + (p.x - c.x) * k, y: c.y + (p.y - c.y) * k }), rot: 0, scale: k });
/** Whether `m` scales heights. */
const scalesHeights = (m: PointMap): m is PointMap & { foot: number } => m.foot != null && m.scale !== 1;
/** Where a base `b` (mm above the ground) goes under a map that scales heights. */
const baseUnder = (m: PointMap & { foot: number }, b: number): number => m.foot + (b - m.foot) * m.scale;

/** What only main.ts knows. */
export interface ObjectServices {
  /** The drawing the objects are in (a ceiling plan's room is looked up there). */
  drawing(): Drawing;
  /** A part's outline as drawn: a room its polygon, a node object its ribbon, a heater block its outer edge. */
  instanceOutline(inst: ProductInstance): Vec2[];
  /** The points a part is grabbed and snapped by: its node line, a room's corners, else its footprint. */
  instancePoints(inst: ProductInstance): Vec2[];
  /** A part's lowest point now (its height from the ground as it stands). */
  instanceBase(inst: ProductInstance): number;
  /** A part's own height, upright (its own, else its product's). */
  instanceHeight(inst: ProductInstance): number;
  /** Re-derive a room after its geometry changed (its benches, carve, fixtures). */
  regenerate(inst: ProductInstance): void;
  /** What stands in a room (its fixtures, the parts in it): what a copy of the room carries. */
  roomContents(room: ProductInstance): ProductInstance[];
  /** A size-locked product keeps its one real size under a scale. */
  resizeLocked(inst: ProductInstance): boolean;
  /** Mirror a tilted part whole across a–b (its tilt mirrors with it); false when it is not tilted. */
  mirrorTilted(inst: ProductInstance, a: Vec2, b: Vec2): boolean;
  connectionPath(c: Connection): Vec2[] | null;
  rcpOutline(v: CeilingPlanView): Vec2[] | null;
  tableOutline(id: string): Vec2[] | null;
  /** A text box's text height when it has none of its own (mm). */
  textHeight: number;
}

/** What a copy is told while its references are pointed at the copies. */
export interface CopyContext {
  /** Original id → its copy's, for every object copied. */
  idMap: ReadonlyMap<string, string>;
  /** A copied room's inner ids (doors, heater walls, benches): original → copy's. */
  featMap: Map<string, string>;
  uid(prefix: string): string;
  /** One fresh id per `id` among these copies (copies relate among themselves). */
  fresh(prefix: string, id: string): string;
  /** Whether the drawing holds an object of this id (an original a copy may keep referring to). */
  has(id: string): boolean;
}

/** What Copy takes of a kind — and so what Cut, which is Copy and then Delete,
 *  keeps of it before it deletes (what Copy does not take, a cut destroys):
 *  - `selection`: its selected objects, each with what it `carries` (a room what
 *    stands in it). A paste selects its copies with the rest — or, `alone`, only
 *    when nothing else was pasted (a section, a ceiling plan: never in a
 *    multi-selection);
 *  - `ends`: none of its own selection — every one whose `ends` are all on
 *    copied objects (a connection, with the parts it joins); a paste does not
 *    select it. */
export type CopyTake<T> =
  | { by: 'selection'; carries?(obj: T): T[]; alone?: true }
  | { by: 'ends'; ends(obj: T): string[] };

/** How a copy is made: what Copy takes, then past its fresh id and its group. */
export interface CopyRule<T> {
  take: CopyTake<T>;
  /** Fresh ids for what a copy holds of its own (a room's doors and benches). */
  fresh?(copy: T, ctx: CopyContext): void;
  /** Point a copy's references at the copies, or let go of them; false leaves the copy out. */
  relink(copy: T, ctx: CopyContext): boolean;
}

/** How Delete takes a kind's objects out of the drawing: each goes when it is
 *  named, and with it what `also` says goes with the objects that go (a room's
 *  fixtures and the projectors its fibre kits feed, its ceiling plans). What
 *  hangs on a deleted object by its id — a connection glued to it, a dimension
 *  snapped to it — is the caller's to prune (deleteSelection.ts: it counts them). */
export interface RemoveRule {
  /** The ids of this kind's objects that go with the objects `gone` names. */
  also?(gone: ReadonlySet<string>, d: Drawing): string[];
}

export interface ObjectAdapter<T> {
  /** A new one's id prefix. */
  readonly prefix: string;
  /** Its outline as drawn: the Parts-tab hover, Zoom to, where a paste centres. */
  outline(obj: T, id: string): Vec2[] | null;
  /** The points it is grabbed and snapped by, and bounded by when selected. */
  points(obj: T): Vec2[];
  /** The point it is placed by — what a move drag puts on the grid (moveStep.ts):
   *  a part's centre, a shape's first vertex, a text box's anchor, an arc's centre. */
  origin(obj: T): Vec2;
  /** The points a move drag snaps it by, when not `points` (an arc's centre and
   *  ends; an ellipse none: its ring is a tessellation, not corners). */
  snapBy?(obj: T): Vec2[];
  move(obj: T, dx: number, dy: number): void;
  /** Map its geometry through `m`. `moved` says whether an object it refers to
   *  goes through the same map (a ceiling plan whose room does keeps its offset). */
  transform(obj: T, m: PointMap, moved?: (id: string) => boolean): void;
  /** Mirror it across the line through a and b. */
  mirror(obj: T, a: Vec2, b: Vec2): void;
  /** Rise by dz mm; null for a kind with no height. */
  raise: ((obj: T, dz: number) => void) | null;
  /** Re-derive what it derives, once its geometry changed (a room's benches). */
  settle(obj: T): void;
  /** How it is copied; null for a kind that is not. */
  copy: CopyRule<T> | null;
  /** How Delete takes it; null for a kind Delete never takes. */
  remove: RemoveRule | null;
}
export type ObjectAdapters = { [K in ItemKind]: ObjectAdapter<KindObjects[K]> };

const norm360 = (d: number): number => ((d % 360) + 360) % 360;
const shift = (dx: number, dy: number) => (p: Vec2): Vec2 => ({ x: p.x + dx, y: p.y + dy });
const centroid = (pts: Vec2[]): Vec2 => ({ x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length });
const bySelection = { by: 'selection' } as const;
const relinkOnly = <T>(take: CopyTake<T>, fn: (o: T, ctx: CopyContext) => boolean): CopyRule<T> => ({ take, relink: fn });
/** Copied as it is: a copy refers to nothing it would follow. */
const noRefs = { take: bySelection, relink: () => true };
/** Goes when it is deleted itself; nothing else takes it along. */
const named: RemoveRule = {};

/** A ring round the points, `pad` mm out: the outline of a line-like object
 *  whose exact path would hatch invisibly thin. */
export function bboxRing(pts: Vec2[], pad: number): Vec2[] | null {
  if (!pts.length) return null;
  const minX = Math.min(...pts.map((p) => p.x)) - pad, maxX = Math.max(...pts.map((p) => p.x)) + pad;
  const minY = Math.min(...pts.map((p) => p.y)) - pad, maxY = Math.max(...pts.map((p) => p.y)) + pad;
  return [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }];
}

/** A few points sampled along an arc, for its bounding box / marquee hit-test. */
export function arcSamplePts(a: Arc): Vec2[] {
  let span = a.endDeg - a.startDeg; while (span < 0) span += 360; if (span < 1e-6) span = 360;
  const pts: Vec2[] = [];
  for (let i = 0; i <= 8; i++) { const ang = (a.startDeg + (span * i) / 8) * Math.PI / 180; pts.push({ x: a.center.x + a.radius * Math.cos(ang), y: a.center.y + a.radius * Math.sin(ang) }); }
  return pts;
}

/** An anchor on a copied object, pointed at the copy; on anything else, let go. */
function copiedAnchor(r: DimAnchor | undefined, idMap: ReadonlyMap<string, string>): DimAnchor | undefined {
  const to = r && idMap.get(anchorObjectId(r));
  if (!r || !to) return undefined;
  if (r.shape) return { ...r, shape: { ...r.shape, id: to } };
  if (r.wall) return { ...r, wall: { ...r.wall, id: to } };
  return { ...r, instId: to };
}

/** A connection's free ends and bends through `fn` (a glued end follows its object). */
function mapConnection(c: Connection, fn: (p: Vec2) => Vec2): void {
  if (!c.from && c.fromEnd?.p) c.fromEnd.p = fn(c.fromEnd.p);
  if (!c.to && c.toEnd?.p) c.toEnd.p = fn(c.toEnd.p);
  if (c.waypoints) c.waypoints = c.waypoints.map((w) => ({ ...fn(w), ...(w.z != null ? { z: w.z } : {}) }));
}

/** A ceiling plan's room, where it stands in the drawing. */
const roomOf = (svc: ObjectServices, v: CeilingPlanView): ProductInstance | undefined => svc.drawing().instances.find((i) => i.id === v.roomId);

export function objectAdapters(svc: ObjectServices): ObjectAdapters {
  const instance: ObjectAdapter<ProductInstance> = {
    prefix: 'inst',
    outline: (i) => svc.instanceOutline(i),
    points: (i) => svc.instancePoints(i),
    origin: (i) => ({ x: i.x, y: i.y }),
    move(i, dx, dy) {
      if (i.nodes && i.nodes.length) i.nodes = i.nodes.map((n) => ({ ...n, x: n.x + dx, y: n.y + dy }));
      i.x += dx; i.y += dy;
    },
    transform(i, m) {
      if (scalesHeights(m)) {
        // Its base about the foot; its height ×k unless size-locked — a room keeps its heights.
        const b = svc.instanceBase(i), nb = baseUnder(m, b);
        if (nb !== b) i.heightFromGround = nb;
        if (!i.autoBenches && !svc.resizeLocked(i)) i.height = svc.instanceHeight(i) * m.scale;
      }
      if (i.nodes && i.nodes.length) {
        i.nodes = i.nodes.map(m.fn);
        ({ x: i.x, y: i.y } = centroid(i.nodes));
      } else {
        const c = m.fn({ x: i.x, y: i.y });
        i.x = c.x; i.y = c.y;
        if (m.scale !== 1 && !svc.resizeLocked(i)) { i.width *= m.scale; if (i.depth != null) i.depth *= m.scale; }
      }
      if (m.rot) i.rotation = norm360(i.rotation + m.rot);
      if (i.polygon && m.scale !== 1) i.polygon = i.polygon.map((q) => ({ x: q.x * m.scale, y: q.y * m.scale }));
    },
    mirror(i, a, b) {
      const r = reflectAcross(a, b);
      if (i.nodes && i.nodes.length) {
        i.nodes = i.nodes.map(r);
        ({ x: i.x, y: i.y } = centroid(i.nodes));
        return;
      }
      if (svc.mirrorTilted(i, a, b)) return;
      const c = r({ x: i.x, y: i.y });
      i.x = c.x; i.y = c.y;
      i.rotation = mirroredRotationDeg(i.rotation, axisAngleDeg(a, b));
      // The room's outline mirrors in its own frame: its walls and benches stay on
      // their walls, mirrored; its ceiling's apex and its lights follow.
      if (i.polygon) { i.polygon = i.polygon.map((q) => ({ x: -q.x, y: q.y })); mirrorCeiling(i); mirrorRoomLights(i); }
    },
    raise: (i, dz) => { i.heightFromGround = svc.instanceBase(i) + dz; },
    settle: (i) => { if (i.autoBenches) svc.regenerate(i); },
    copy: {
      // A room copies with everything standing in it (its fixtures included), and a
      // product standing in it whole.
      take: {
        by: 'selection',
        carries(room) {
          if (!room.autoBenches) return [];
          const d = svc.drawing(), out: ProductInstance[] = [];
          for (const c of svc.roomContents(room)) {
            out.push(c);
            const g = outermostGroup(d, c);
            if (g) for (const m of groupMembers(d, g)) { const inst = d.instances.find((i) => i.id === m.id); if (inst) out.push(inst); }
          }
          return out;
        },
      },
      fresh(c, ctx) {
        // A copy's doors, panes and benches are its own (selecting one must not light the original's).
        for (const [a, b] of remintFeatureIds(c, ctx.uid)) ctx.featMap.set(a, b);
        delete c.groupName;
        if (c.relId) c.relId = ctx.fresh('rel', c.relId);
      },
      relink(c, ctx) {
        // A room's own fixtures follow the copied room; a fibre kit feeds the
        // copy of its projector or none; an anchor to a room not copied is let
        // go — a copy never shares its original's.
        const fx = c.roomFixture && ctx.idMap.get(c.roomFixture.roomId);
        if (c.roomFixture && fx) c.roomFixture.roomId = fx;
        relinkKits([c], ctx.idMap);
        const ra = c.roomAnchor && ctx.idMap.get(c.roomAnchor.roomId);
        if (c.roomAnchor && ra) c.roomAnchor.roomId = ra; else delete c.roomAnchor;
        const cs = c.cubeSnap && ctx.idMap.get(c.cubeSnap.roomId);
        if (c.cubeSnap && cs) c.cubeSnap.roomId = cs; else delete c.cubeSnap;
        return true;
      },
    },
    // A room's fixtures go with it, and the projectors its fibre kits feed.
    remove: {
      also: (gone, d) => [
        ...d.instances.filter((i) => i.roomFixture && gone.has(i.roomFixture.roomId)).map((i) => i.id),
        ...kitProjectorIds(d.instances.filter((i) => gone.has(i.id))),
      ],
    },
  };

  const polygon: ObjectAdapter<Polygon> = {
    prefix: 'poly',
    outline: (p) => (p.points.length >= 3 ? p.points : null),
    points: (p) => p.points,
    origin: (p) => p.points[0] ?? { x: 0, y: 0 },
    snapBy: (p) => (p.ellipse ? [] : p.points),
    move: (p, dx, dy) => polygon.transform(p, translation(dx, dy)),
    transform(p, m) {
      // The points ring carries an ellipse too (ellipse is a render flag).
      p.points = p.points.map(m.fn);
      if (p.holes) p.holes = p.holes.map((h) => h.map(m.fn));
      if (scalesHeights(m)) {
        const b = polygonBase(p), nb = baseUnder(m, b), h = polygonHeight(p);
        if (nb !== b) p.heightFromGround = nb;
        if (h) p.height = h * m.scale;
      }
    },
    mirror(p, a, b) {
      const r = reflectAcross(a, b);
      p.points = p.points.map(r).reverse(); // rings reversed so their winding survives
      if (p.holes) p.holes = p.holes.map((h) => h.map(r).reverse());
    },
    raise: (p, dz) => { p.heightFromGround = polygonBase(p) + dz; },
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const wall: ObjectAdapter<Wall> = {
    prefix: 'wall',
    outline: (w) => (w.points.length >= 2 ? (w.style === 'line' ? bboxRing(w.points, 60) : wallBand(w.points, w.thickness, w.align)) : null),
    points: (w) => wallSnapVerts(w),
    origin: (w) => w.points[0] ?? { x: 0, y: 0 },
    move: (w, dx, dy) => { w.points = w.points.map(shift(dx, dy)); },
    transform(w, m) {
      w.points = w.points.map(m.fn);
      if (m.scale !== 1) w.thickness *= m.scale;
      if (scalesHeights(m)) {
        const b = wallBase(w), nb = baseUnder(m, b);
        if (nb !== b) w.heightFromGround = nb;
        w.height = wallHeight(w) * m.scale;
      }
    },
    mirror: (w, a, b) => { w.points = w.points.map(reflectAcross(a, b)); },
    raise: (w, dz) => { w.heightFromGround = wallBase(w) + dz; },
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const flooring: ObjectAdapter<Flooring> = {
    prefix: 'floor',
    outline: (f) => (f.points.length >= 3 ? f.points : null),
    points: (f) => f.points,
    origin: (f) => f.points[0] ?? { x: 0, y: 0 },
    move: (f, dx, dy) => flooring.transform(f, translation(dx, dy)),
    transform(f, m) {
      f.points = f.points.map(m.fn);
      if (f.drain) { const c = m.fn(f.drain); f.drain.x = c.x; f.drain.y = c.y; if (m.rot) f.drain.rotation = norm360(f.drain.rotation + m.rot); }
    },
    mirror(f, a, b) {
      const r = reflectAcross(a, b);
      f.points = f.points.map(r).reverse();
      if (f.drain) { const c = r(f.drain); f.drain.x = c.x; f.drain.y = c.y; f.drain.rotation = norm360(2 * axisAngleDeg(a, b) - f.drain.rotation); }
    },
    raise: null,
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const conn: ObjectAdapter<Connection> = {
    prefix: 'con',
    outline: (c) => { const path = svc.connectionPath(c); return path ? bboxRing(path, 60) : null; },
    points: (c) => [
      ...(!c.from && c.fromEnd?.p ? [c.fromEnd.p] : []),
      ...(c.waypoints ?? []).map((w) => ({ x: w.x, y: w.y })),
      ...(!c.to && c.toEnd?.p ? [c.toEnd.p] : []),
    ],
    origin: (c) => conn.points(c)[0] ?? { x: 0, y: 0 },
    move: (c, dx, dy) => mapConnection(c, shift(dx, dy)),
    transform: (c, m) => mapConnection(c, m.fn),
    mirror: (c, a, b) => mapConnection(c, reflectAcross(a, b)),
    raise: null,
    settle: () => {},
    // Copied with the objects it joins: an end on an object not copied leaves it out.
    copy: relinkOnly({ by: 'ends', ends: (c) => [c.from, c.to].filter((id): id is string => !!id) }, (c, ctx) => {
      for (const which of ['from', 'to'] as const) {
        const id = c[which];
        if (!id) continue;
        const to = ctx.idMap.get(id);
        if (!to) return false;
        c[which] = to;
        const end = which === 'from' ? c.fromEnd : c.toEnd;
        if (end?.ref) {
          if (end.ref.shape) end.ref.shape.id = to;
          else if (end.ref.wall) end.ref.wall.id = to;
          else end.ref.instId = to;
        }
        // A door / heater wall of a copied room has a fresh id (`remintFeatureIds`).
        if (end?.on && (end.on.kind === 'door' || end.on.kind === 'heaterWall')) end.on.id = ctx.featMap.get(end.on.id) ?? end.on.id;
      }
      return true;
    }),
    remove: named,
  };

  const section: ObjectAdapter<SectionLine> = {
    prefix: 'sec',
    outline: (s) => bboxRing([s.a, s.b], 150),
    points: (s) => [s.a, s.b, ...(s.viewOrigin ? [s.viewOrigin] : [])],
    origin: (s) => s.a,
    move: (s, dx, dy) => section.transform(s, translation(dx, dy)),
    transform(s, m) {
      s.a = m.fn(s.a); s.b = m.fn(s.b);
      if (s.viewOrigin) s.viewOrigin = m.fn(s.viewOrigin);
      if (m.rot && s.viewAngle != null) s.viewAngle += (m.rot * Math.PI) / 180;
    },
    mirror(s, a, b) {
      const r = reflectAcross(a, b);
      s.a = r(s.a); s.b = r(s.b);
      // Its line reflected, its normal turns over: the side it looks toward
      // flips so it looks the mirrored way.
      s.side = s.side === 1 ? -1 : 1;
      if (s.viewOrigin) s.viewOrigin = r(s.viewOrigin);
      if (s.viewAngle != null) s.viewAngle = (2 * axisAngleDeg(a, b) * Math.PI) / 180 - s.viewAngle;
    },
    raise: null,
    settle: () => {},
    // A new section: its letter comes from the order (the next one).
    copy: { take: { by: 'selection', alone: true }, fresh: (s) => { delete s.label; delete s.below; }, relink: () => true },
    remove: named,
  };

  // A view stands at its room's plan moved by `offset`. Moved WITH its room it
  // keeps its offset (turned and scaled as the room is); moved alone, its place
  // goes through the map and the offset is what it is from the room.
  const rcpPlace = (v: CeilingPlanView): Vec2 => { const r = roomOf(svc, v); return { x: (r?.x ?? 0) + v.offset.x, y: (r?.y ?? 0) + v.offset.y }; };
  const rcpSetPlace = (v: CeilingPlanView, p: Vec2): void => { const r = roomOf(svc, v); v.offset = { x: p.x - (r?.x ?? 0), y: p.y - (r?.y ?? 0) }; };
  const rcp: ObjectAdapter<CeilingPlanView> = {
    prefix: 'rcp',
    outline: (v) => svc.rcpOutline(v),
    points: (v) => [rcpPlace(v)],
    origin: (v) => rcpPlace(v),
    move: (v, dx, dy) => { v.offset = { x: v.offset.x + dx, y: v.offset.y + dy }; },
    transform(v, m, moved) {
      if (moved?.(v.roomId)) { const o = rotatePoint(v.offset, m.rot); v.offset = { x: o.x * m.scale, y: o.y * m.scale }; return; }
      rcpSetPlace(v, m.fn(rcpPlace(v)));
    },
    mirror: (v, a, b) => rcpSetPlace(v, reflectAcross(a, b)(rcpPlace(v))),
    raise: null,
    settle: () => {},
    // Another view of the same room — of the copied room when that is copied too;
    // a view whose room is not in the drawing is left out.
    copy: relinkOnly({ by: 'selection', alone: true }, (v, ctx) => {
      const to = ctx.idMap.get(v.roomId);
      if (to) { v.roomId = to; return true; }
      return ctx.has(v.roomId);
    }),
    // A view goes with its room.
    remove: { also: (gone, d) => (d.ceilingPlans ?? []).filter((v) => gone.has(v.roomId)).map((v) => v.id) },
  };

  const text: ObjectAdapter<TextComment> = {
    prefix: 'text',
    outline: (t) => textBoxCorners(t),
    points: (t) => [...textBoxCorners(t), ...(t.leader ? [t.leader] : [])],
    origin: (t) => ({ x: t.x, y: t.y }),
    move(t, dx, dy) { t.x += dx; t.y += dy; if (t.leader) t.leader = { x: t.leader.x + dx, y: t.leader.y + dy }; },
    transform(t, m) {
      const c = textBoxCentre(t);
      if (m.scale !== 1) { t.w *= m.scale; t.h *= m.scale; t.heightMm = (t.heightMm ?? svc.textHeight) * m.scale; }
      if (m.rot) rotateTextBoxTo(t, (t.rotation ?? 0) + m.rot); // turns with the selection
      moveTextBoxCentreTo(t, m.fn(c));
      if (t.leader) t.leader = m.fn(t.leader);
    },
    mirror(t, a, b) {
      const r = reflectAcross(a, b);
      moveTextBoxCentreTo(t, r(textBoxCentre(t))); // mirrored in place, still readable
      if (t.leader) t.leader = r(t.leader);
    },
    raise: null,
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const arc: ObjectAdapter<Arc> = {
    prefix: 'arc',
    outline: (a) => bboxRing(arcSamplePts(a), 40),
    points: (a) => arcSamplePts(a),
    origin: (a) => a.center,
    snapBy: (a) => { const s = arcSamplePts(a); return [a.center, s[0], s[s.length - 1]]; },
    move: (a, dx, dy) => { a.center = { x: a.center.x + dx, y: a.center.y + dy }; },
    transform(a, m) {
      a.center = m.fn(a.center);
      if (m.scale !== 1) a.radius *= m.scale;
      if (m.rot) { a.startDeg += m.rot; a.endDeg += m.rot; } // its sweep turns with it
    },
    mirror(a, p, q) {
      // Angles reflect as 2θ − φ, start and end swapped.
      const theta = axisAngleDeg(p, q);
      a.center = reflectAcross(p, q)(a.center);
      const s0 = 2 * theta - a.endDeg, e0 = 2 * theta - a.startDeg;
      a.startDeg = norm360(s0); a.endDeg = norm360(e0);
    },
    raise: null,
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const leader: ObjectAdapter<Leader> = {
    prefix: 'leader',
    outline: (l) => (l.points.length ? bboxRing(l.points, 80) : null),
    points: (l) => l.points,
    origin: (l) => l.points[0] ?? { x: 0, y: 0 },
    move: (l, dx, dy) => { l.points = l.points.map(shift(dx, dy)); },
    transform: (l, m) => { l.points = l.points.map(m.fn); },
    mirror: (l, a, b) => { l.points = l.points.map(reflectAcross(a, b)); },
    raise: null,
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const cloud: ObjectAdapter<RevisionCloud> = {
    prefix: 'cloud',
    outline: (c) => (c.points.length >= 3 ? c.points : null),
    points: (c) => c.points,
    origin: (c) => c.points[0] ?? { x: 0, y: 0 },
    move: (c, dx, dy) => { c.points = c.points.map(shift(dx, dy)); },
    transform: (c, m) => { c.points = c.points.map(m.fn); },
    mirror: (c, a, b) => { c.points = c.points.map(reflectAcross(a, b)).reverse(); },
    raise: null,
    settle: () => {},
    copy: noRefs,
    remove: named,
  };

  const dim: ObjectAdapter<DimensionLine> = {
    prefix: 'dim',
    outline: (d) => bboxRing([d.a, d.b], 100),
    points: (d) => [d.a, d.b],
    origin: (d) => d.a,
    move: (d, dx, dy) => dim.transform(d, translation(dx, dy)),
    transform: (d, m) => { d.a = m.fn(d.a); d.b = m.fn(d.b); },
    mirror: (d, a, b) => { const r = reflectAcross(a, b); d.a = r(d.a); d.b = r(d.b); },
    raise: null,
    settle: () => {},
    // An end anchored to a copied object follows the COPY; one anchored to
    // anything else is let go, or the copy would snap straight back onto the
    // original.
    copy: relinkOnly(bySelection, (d, ctx) => {
      const a = copiedAnchor(d.aRef, ctx.idMap), b = copiedAnchor(d.bRef, ctx.idMap);
      if (a) d.aRef = a; else delete d.aRef;
      if (b) d.bRef = b; else delete d.bRef;
      return true;
    }),
    remove: named,
  };

  const table: ObjectAdapter<ScheduleTable> = {
    prefix: 'table',
    outline: (_t, id) => svc.tableOutline(id),
    points: () => [],
    origin: () => ({ x: 0, y: 0 }),
    move: () => {},
    transform: () => {},
    mirror: () => {},
    raise: null,
    settle: () => {},
    // Not in an array: the drawing has one of each, neither copied nor deleted.
    copy: null,
    remove: null,
  };

  return { arc, cloud, conn, dim, flooring, instance, leader, polygon, rcp, section, table, text, wall };
}

const loose = (A: ObjectAdapters, kind: ItemKind): ObjectAdapter<unknown> => A[kind] as unknown as ObjectAdapter<unknown>;
const idOf = (o: unknown): string => (o as { id: string }).id;

/** An object's outline (by kind, the object as stored). */
export function outlineOf(A: ObjectAdapters, kind: ItemKind, obj: unknown, id: string): Vec2[] | null {
  return loose(A, kind).outline(obj, id);
}

/** Map these objects through `m`, then let each re-derive what it derives. */
export function transformItems(A: ObjectAdapters, items: readonly ObjItem[], m: PointMap): void {
  const ids = new Set(items.map((it) => idOf(it.obj)));
  for (const it of items) loose(A, it.kind).transform(it.obj, m, (id) => ids.has(id));
  for (const it of items) loose(A, it.kind).settle(it.obj);
}

/** Mirror these objects across the line through a and b, then let each re-derive. */
export function mirrorItems(A: ObjectAdapters, items: readonly ObjItem[], a: Vec2, b: Vec2): void {
  for (const it of items) loose(A, it.kind).mirror(it.obj, a, b);
  for (const it of items) loose(A, it.kind).settle(it.obj);
}

/** One object as a uniform handle: its points, the point it is placed by and
 *  the ones it snaps by, a move (re-derived after), a rise. */
export function movableOf(A: ObjectAdapters, it: ObjItem): { id: string; verts: () => Vec2[]; origin: () => Vec2; snaps: () => Vec2[]; move: (dx: number, dy: number) => void; raise: (dz: number) => void } {
  const a = loose(A, it.kind), o = it.obj;
  return {
    id: idOf(o),
    verts: () => a.points(o),
    origin: () => a.origin(o),
    snaps: () => (a.snapBy ? a.snapBy(o) : a.points(o)),
    move: (dx, dy) => { a.move(o, dx, dy); a.settle(o); },
    raise: (dz) => { if (dz) a.raise?.(o, dz); },
  };
}

/** What a copy is made from: objects of any stored kind, the groups copied whole
 *  and the constraints between them. */
export type CopyPayload = Partial<ObjectArrays> & { groups?: DrawingGroup[]; constraints?: ObjectConstraint[] };

export interface CopyOptions {
  uid(prefix: string): string;
  /** Each copy rises this far (mm): a section array's rows, Transform's Δz. */
  rise?: number;
  /** Runs on the copies once they are in the drawing, before they settle. */
  prepare?(added: Partial<ObjectArrays>): void;
}

export interface CopyResult {
  /** Original id → copy id, for every object and group copied. */
  idMap: Map<string, string>;
  /** The copies now in the drawing, kind by kind. */
  added: Partial<ObjectArrays>;
  /** Copies left out: what they refer to is neither copied nor in the drawing. */
  left: ObjItem[];
}

/** The constraints all of whose objects were copied, pointed at the copies (not
 *  Fix: a copy is free to be put anywhere). */
function copiedConstraints(cs: readonly ObjectConstraint[], idMap: ReadonlyMap<string, string>, uid: (p: string) => string): ObjectConstraint[] {
  const refs = (c: ObjectConstraint) => [c.a, ...(c.b ? [c.b] : []), ...(c.m ? [c.m] : [])];
  return cs.filter((c) => c.type !== 'fix' && refs(c).every((r) => idMap.has(r.obj))).map((c) => ({
    ...structuredClone(c), id: uid('con'),
    a: { ...c.a, obj: idMap.get(c.a.obj)! },
    ...(c.b ? { b: { ...c.b, obj: idMap.get(c.b.obj)! } } : {}),
    ...(c.m ? { m: { ...c.m, obj: idMap.get(c.m.obj)! } } : {}),
  }));
}

/** Copies of the groups copied whole, fresh ids, nesting kept. One the user
 *  named keeps its name; one the app once named ("Group NN", a payload saved
 *  before) none, so it shows its default. Returns old → new id. */
function adoptGroups(d: Drawing, entries: readonly DrawingGroup[], uid: (p: string) => string): Map<string, string> {
  const map = new Map(entries.map((g) => [g.id, uid('grp')]));
  for (const g of entries) {
    const copy: DrawingGroup = { ...structuredClone(g), id: map.get(g.id)! };
    if (g.parent && map.has(g.parent)) copy.parent = map.get(g.parent); else delete copy.parent;
    if (g.name && isAutoGroupName(g.name)) delete copy.name;
    (d.groups ??= []).push(copy);
  }
  return map;
}

/**
 * THE copy: add copies of a payload's objects to the drawing, mapped through
 * `m` — paste, Duplicate, a Ctrl-drag, Mirror's copy, the arrays and Transform ▸
 * Copy all come here. Every copy gets a fresh id; a group copied whole becomes
 * a fresh group (a payload from before the registry carries bare group ids:
 * each gets a fresh one); each kind's `copy` rule points its references at the
 * copies — a dimension's anchors, a connection's ends, a room's fixtures and
 * fibre kits, a ceiling plan's room — or lets go of them; the constraints among
 * the copies come along. Copies relate among themselves, never to the originals.
 * Stacking, layers and selection stay with the caller.
 */
export function copyObjects(d: Drawing, cb: CopyPayload, m: PointMap, A: ObjectAdapters, opts: CopyOptions): CopyResult {
  const idMap = new Map<string, string>();
  const gidMap = adoptGroups(d, cb.groups ?? [], opts.uid);
  const regroup = (g: string | undefined): string | undefined => {
    if (!g) return undefined;
    if (!gidMap.has(g)) gidMap.set(g, opts.uid('grp'));
    return gidMap.get(g);
  };
  const freshIds = new Map<string, string>();
  const existing = new Set(STORED_KINDS.flatMap((k) => (objectsOf(d, k) ?? []).map(idOf)));
  const ctx: CopyContext = {
    idMap, featMap: new Map(), uid: opts.uid, has: (id) => existing.has(id),
    fresh: (prefix, id) => { if (!freshIds.has(id)) freshIds.set(id, opts.uid(prefix)); return freshIds.get(id)!; },
  };
  const copies: ObjItem[] = [];
  for (const kind of STORED_KINDS) {
    if (!A[kind].copy) continue;
    for (const src of objectsOf(cb, kind) ?? []) {
      const copy = structuredClone(src) as { id: string; groupId?: string };
      copy.id = opts.uid(A[kind].prefix);
      idMap.set(src.id, copy.id);
      if ('groupId' in copy) { const g = regroup(copy.groupId); if (g) copy.groupId = g; else delete copy.groupId; }
      copies.push({ kind, obj: copy } as ObjItem);
    }
  }
  const rule = (it: ObjItem): CopyRule<unknown> => loose(A, it.kind).copy!;
  for (const it of copies) rule(it).fresh?.(it.obj, ctx);
  const kept = copies.filter((it) => rule(it).relink(it.obj, ctx));
  const copied = new Set(idMap.values());
  for (const it of kept) {
    const a = loose(A, it.kind);
    a.transform(it.obj, m, (id) => copied.has(id));
    if (opts.rise) a.raise?.(it.obj, opts.rise);
  }
  const added: Partial<Record<ArrayKey, unknown[]>> = {};
  for (const it of kept) {
    const key = OBJECT_ARRAY[it.kind as StoredKind];
    const into = d as unknown as Record<ArrayKey, unknown[] | undefined>;
    (into[key] ??= []).push(it.obj);
    (added[key] ??= []).push(it.obj);
  }
  const cons = copiedConstraints(cb.constraints ?? [], idMap, opts.uid);
  if (cons.length) (d.constraints ??= []).push(...cons);
  pruneRelations(d.instances); // a copy related to nothing copied keeps no relation
  normalizeGroups(d);
  for (const [g, c] of gidMap) idMap.set(g, c);
  opts.prepare?.(added as Partial<ObjectArrays>);
  for (const it of kept) loose(A, it.kind).settle(it.obj);
  return { idMap, added: added as Partial<ObjectArrays>, left: copies.filter((it) => !kept.includes(it)) };
}

/** What Copy puts on the clipboard for a selection (ids by kind): clones, kind
 *  by kind as each adapter's `copy.take` says — a kind taken by its selection,
 *  its selected objects with what each carries (in the drawing's order); a kind
 *  taken by its ends, every one whose ends are all on those (none of its own
 *  selection); a kind not copied, nothing. Cut is Copy and then Delete: what is
 *  not taken here, a cut destroys. */
export function copiesOfSelection(d: Drawing, sel: Partial<Record<ItemKind, readonly string[]>>, A: ObjectAdapters): ObjectArrays {
  const out = emptyObjectArrays() as unknown as Record<ArrayKey, unknown[]>;
  const copied = new Set<string>();
  const take = (kind: StoredKind, keep: (o: unknown) => boolean): void => {
    const objs = (objectsOf(d, kind) ?? []).filter(keep);
    for (const o of objs) copied.add(idOf(o));
    out[OBJECT_ARRAY[kind]] = objs.map((o) => structuredClone(o));
  };
  for (const kind of STORED_KINDS) {
    const rule = loose(A, kind).copy?.take;
    const ids = new Set(sel[kind] ?? []);
    if (rule?.by !== 'selection' || !ids.size) continue;
    const want = new Set<string>();
    for (const o of objectsOf(d, kind) ?? []) {
      if (!ids.has(idOf(o))) continue;
      want.add(idOf(o));
      for (const c of rule.carries?.(o) ?? []) want.add(idOf(c));
    }
    take(kind, (o) => want.has(idOf(o)));
  }
  for (const kind of STORED_KINDS) {
    const rule = loose(A, kind).copy?.take;
    if (rule?.by !== 'ends') continue;
    take(kind, (o) => { const ends = rule.ends(o); return ends.length > 0 && ends.every((id) => copied.has(id)); });
  }
  return out as unknown as ObjectArrays;
}

/** What a paste selects of its copies (each adapter's `copy.take`): the copies of
 *  every kind taken by its selection, together; a kind selected only `alone` (a
 *  section, a ceiling plan) only when nothing else was pasted — the last copy of
 *  the first such kind; a kind copied by its ends (a connection), none. */
export function pastedSelection(added: Partial<ObjectArrays>, A: ObjectAdapters): { sel: Partial<Record<ItemKind, string[]>>; alone: { kind: StoredKind; id: string } | null } {
  const sel: Partial<Record<ItemKind, string[]>> = {};
  let alone: { kind: StoredKind; id: string } | null = null, others = false;
  for (const kind of STORED_KINDS) {
    const ids = (objectsOf(added, kind) ?? []).map(idOf);
    if (!ids.length) continue;
    const rule = loose(A, kind).copy?.take;
    if (rule?.by === 'selection' && rule.alone) { alone ??= { kind, id: ids[ids.length - 1] }; continue; }
    others = true;
    if (rule?.by === 'selection') sel[kind] = ids;
  }
  return { sel, alone: others ? null : alone };
}

/** THE delete's removal: the objects `ids` names out of the drawing, kind by kind
 *  as each adapter's `remove` says, with what goes with them (a room's fixtures
 *  and the projectors its kits feed, its ceiling plans — until nothing more
 *  goes). Returns what was removed, in the drawing's order. The connections and
 *  dimensions on them, the relations, groups and rooms they leave are the
 *  caller's (deleteSelection.ts `removeObjectsById`). */
export function removeObjects(d: Drawing, ids: ReadonlySet<string>, A: ObjectAdapters): StoredItem[] {
  const gone = new Set(ids);
  for (let grew = true; grew;) {
    grew = false;
    for (const kind of STORED_KINDS) for (const id of A[kind].remove?.also?.(gone, d) ?? []) if (!gone.has(id)) { gone.add(id); grew = true; }
  }
  const into = d as unknown as Record<ArrayKey, unknown[] | undefined>;
  const removed: StoredItem[] = [];
  for (const kind of STORED_KINDS) {
    const key = OBJECT_ARRAY[kind], arr = into[key];
    if (!A[kind].remove || !arr) continue;
    for (const o of arr) if (gone.has(idOf(o))) removed.push({ kind, obj: o } as StoredItem);
    into[key] = arr.filter((o) => !gone.has(idOf(o)));
  }
  return removed;
}
